/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import Keycloak, { 
  type KeycloakTokenParsed, 
  type KeycloakLoginOptions, 
  type KeycloakLogoutOptions 
} from "keycloak-js"
import { getKeycloakConfig, KeycloakConfigError, type KeycloakConfig } from "../config/keycloak"
import { getKeycloakInstance } from "./keycloak"

type AuthContextValue = {
  isAuthenticated: boolean
  isInitializing: boolean
  token?: string
  parsedToken?: KeycloakTokenParsed
  login: (options?: KeycloakLoginOptions) => Promise<void>
  logout: (options?: KeycloakLogoutOptions) => Promise<void>
  changePassword: () => Promise<void>
  refreshToken: (minValidity?: number) => Promise<boolean>
  keycloak: Keycloak | null
  error: Error | null
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

type AuthProviderProps = {
  children: ReactNode
}

const REFRESH_INTERVAL_MS = 30_000
const DEFAULT_MIN_VALIDITY = 60

export function AuthProvider({ children }: Readonly<AuthProviderProps>) {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null)
  const [keycloakSettings, setKeycloakSettings] = useState<KeycloakConfig | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)
  const [token, setToken] = useState<string | undefined>(undefined)
  const [parsedToken, setParsedToken] = useState<KeycloakTokenParsed | undefined>(undefined)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const currentPath = globalThis.location.pathname;
    if (currentPath !== '/' && !currentPath.startsWith('/auth/callback')) {
      sessionStorage.setItem('returnUrl', currentPath);
    }
  }, []);

  useEffect(() => {
    let isMounted = true
    let resolvedConfig: KeycloakConfig
    try {
      resolvedConfig = getKeycloakConfig()
      if (isMounted) {
        queueMicrotask(() => {
          setKeycloakSettings(resolvedConfig)
        })
      }
    } catch (configError) {
      if (isMounted) {
        queueMicrotask(() => {
          setError(configError instanceof Error ? configError : new Error(String(configError)))
          setIsInitializing(false)
        })
      }
      return () => { isMounted = false }
    }

    try {
      const instance = getKeycloakInstance(resolvedConfig)
      if (isMounted) {
        queueMicrotask(() => {
          setKeycloak(instance)
        })
      }
    } catch (loaderError) {
      if (isMounted) {
        queueMicrotask(() => {
          setError(loaderError instanceof Error ? loaderError : new Error(String(loaderError)))
          setIsInitializing(false)
        })
      }
    }

    return () => { isMounted = false }
  }, [])

  useEffect(() => {
    if (!keycloak || !keycloakSettings) return undefined
    let isMounted = true

    const updateTokenState = () => {
      if (!isMounted) return
      setToken(keycloak.token ?? undefined)
      setParsedToken(keycloak.tokenParsed ?? undefined)
    }

    const handleAuthSuccess = () => {
      if (!isMounted) return
      setIsAuthenticated(true)
      setError(null)
      updateTokenState()
    }

    const handleAuthLogout = () => {
      if (!isMounted) return
      setIsAuthenticated(false)
      setToken(undefined)
      setParsedToken(undefined)
    }

    const loginWithConfiguredRedirect = (options?: KeycloakLoginOptions) => {
      sessionStorage.setItem('returnUrl', globalThis.location.pathname)
      return keycloak.login({ redirectUri: keycloakSettings.redirectUri, ...options })
    }

    Object.assign(keycloak, {
      onAuthSuccess: handleAuthSuccess,
      onAuthRefreshSuccess: updateTokenState,
      onAuthLogout: handleAuthLogout,
      onTokenExpired: () => {
        keycloak
          .updateToken(0)
          .then((refreshed: boolean) => {
            if (refreshed) updateTokenState()
          })
          .catch(() => {
            loginWithConfiguredRedirect().catch((loginError: unknown) => {
              setError(loginError instanceof Error ? loginError : new Error(String(loginError)))
            })
          })
      }
    })

    const init = async () => {
      try {
        const savedUrl = sessionStorage.getItem('returnUrl');
        const targetUri = savedUrl 
          ? `${globalThis.location.origin}${savedUrl}` 
          : keycloakSettings.redirectUri;
        
        const authenticated = await keycloak.init({
          onLoad: "login-required",
          pkceMethod: "S256",
          redirectUri: targetUri,
          checkLoginIframe: false,
        })

        if (authenticated) sessionStorage.removeItem('returnUrl');
        if (!isMounted) return
        setIsAuthenticated(authenticated)
        if (authenticated) updateTokenState()
      } catch (initError) {
        if (isMounted) {
          setError(initError instanceof Error ? initError : new Error(String(initError)))
        }
      } finally {
        if (isMounted) setIsInitializing(false)
      }
    }

    init()

    const refreshInterval = globalThis.setInterval(() => {
      if (!keycloak.authenticated) return
      keycloak
        .updateToken(DEFAULT_MIN_VALIDITY)
        .then((refreshed: boolean) => {
          if (refreshed) updateTokenState()
        })
        .catch(() => {
          handleAuthLogout()
          loginWithConfiguredRedirect().catch((loginError: unknown) => {
            setError(loginError instanceof Error ? loginError : new Error(String(loginError)))
          })
        })
    }, REFRESH_INTERVAL_MS)

    return () => {
      isMounted = false
      globalThis.clearInterval(refreshInterval)
      Object.assign(keycloak, {
        onAuthSuccess: undefined,
        onAuthRefreshSuccess: undefined,
        onAuthLogout: undefined,
        onTokenExpired: undefined
      })
    }
  }, [keycloak, keycloakSettings])

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isInitializing,
      token,
      parsedToken,
      error,
      login: (options) => {
        if (!keycloak || !keycloakSettings) return Promise.reject(new Error("Keycloak not initialized"))
        return keycloak.login({ redirectUri: keycloakSettings.redirectUri, ...options })
      },
      logout: (options) => {
        if (!keycloak || !keycloakSettings) return Promise.reject(new Error("Keycloak not initialized"))
        return keycloak.logout({ redirectUri: keycloakSettings.redirectUri, ...options })
      },
      changePassword: () => {
        if (!keycloak || !keycloakSettings) return Promise.reject(new Error("Keycloak not initialized"))
        sessionStorage.setItem('returnUrl', globalThis.location.pathname)
        return keycloak.login({ 
          action: 'UPDATE_PASSWORD', 
          redirectUri: keycloakSettings.redirectUri 
        })
      },
      refreshToken: (minValidity = DEFAULT_MIN_VALIDITY) => {
        if (!keycloak) return Promise.reject(new Error("Keycloak not initialized"))
        return keycloak.updateToken(minValidity)
      },
      keycloak,
    }),
    [isAuthenticated, isInitializing, token, parsedToken, error, keycloak, keycloakSettings],
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) throw new Error("useAuth must be used within an AuthProvider")
  return context
}

type AuthGateProps = {
  children: ReactNode
  fallback?: ReactNode
  errorFallback?: (error: Error) => ReactNode
}

export function AuthGate({ children, fallback, errorFallback }: Readonly<AuthGateProps>) {
  const { isInitializing, isAuthenticated, login, error, keycloak } = useAuth()
  
  const defaultErrorFallback = () => {
    if (!error) return null
    if (error instanceof KeycloakConfigError) {
      return (
        <div style={{ padding: "2rem", maxWidth: "640px" }}>
          <h2>Configurazione Keycloak incompleta</h2>
          <p>{error.message}</p>
        </div>
      )
    }
    return (
      <div style={{ padding: "2rem" }}>
        <p>Si è verificato un errore durante l&apos;autenticazione.</p>
        <pre style={{ whiteSpace: "pre-wrap" }}>{error.message}</pre>
        {keycloak ? (
          <button type="button" onClick={() => { void login() }}>Riprova</button>
        ) : (
          <button type="button" onClick={() => globalThis.location.reload()}>Ricarica la pagina</button>
        )}
      </div>
    )
  }

  if (error) return errorFallback?.(error) ?? defaultErrorFallback()
  if (isInitializing || !keycloak) return (fallback) ?? <div className="loading-container">Connessione...</div>
  if (!isAuthenticated) return <div className="loading-container">Reindirizzamento...</div>

  return <>{children}</>
}